import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { RouteLine } from '../../../types/climb';
import { useZoomPan } from '../board-grid/useZoomPan';
import { clampFraction } from '../photo-hold-roles';
import {
  DEFAULT_ROUTE_CORRIDOR,
  DEFAULT_ROUTE_THICKNESS,
  isDrawableLine,
  lineSegmentAt,
  lineVertexAt,
} from '../route-line';

interface Point {
  x: number;
  y: number;
}

export interface PhotoLineInput {
  /** Natural size of the photo, from the <img> that renders it. */
  naturalWidth: number;
  naturalHeight: number;
  line: RouteLine | null;
  onChange: (line: RouteLine | null) => void;
  /**
   * Whether this surface sets the corridor — the band of wall left lit around
   * the line. On a gym wall it answers "which of these holds are mine"; on rock
   * there are no holds to tell apart and the line is the whole topo, so the crag
   * editor turns it off and the lines it saves carry `corridor: 0`, which every
   * renderer reads as "leave the photograph alone".
   */
  allowCorridor?: boolean;
}

export type PhotoLineController = ReturnType<typeof usePhotoLine>;

/**
 * Drawing the line of a route on its photo.
 *
 * A route is shot from the ground, where its holds are too small to tell apart
 * and too many to circle one by one, so it is marked as a path instead. A press
 * on the wall carries the line on towards the anchor. A press on a vertex takes
 * hold of it, and a press on the line between two vertices makes one there and
 * takes hold of that — which is how a straight line is bent into a traverse
 * without having to place a point and then go back for it.
 *
 * As with the circles, one finger is the pen and never pans: the picture is
 * moved and scaled with two. The hit tolerances are divided by the zoom, so a
 * vertex stays as easy to grab close up as it is at rest.
 */
export function usePhotoLine({
  naturalWidth,
  naturalHeight,
  line,
  onChange,
  allowCorridor = true,
}: PhotoLineInput) {
  const zoom = useZoomPan();
  const [dragging, setDragging] = useState<number | null>(null);
  // Which vertex the controls act on. A press picks one, and it is what the
  // delete button removes — the line is edited where it is wrong, rather than
  // only ever from its far end.
  const [selected, setSelected] = useState<number | null>(null);
  // Set once the press has actually moved a vertex, so releasing it does not
  // also read as a tap on the wall and drop a point on top.
  const movedRef = useRef(false);
  // The fingers currently down, and whether this gesture ever had more than one
  // — that is the picture being moved, and it must not leave a point where the
  // first finger landed. Counted rather than read off `event.isPrimary`, which
  // not every source of pointer events fills in.
  const activePointers = useRef(new Set<number>());
  const multiTouchRef = useRef(false);

  const points = line?.points ?? [];
  // A surface with no corridor control saves 0 — "leave the photograph alone" —
  // rather than the default band nobody asked for.
  const corridor = line?.corridor ?? (allowCorridor ? DEFAULT_ROUTE_CORRIDOR : 0);
  const thickness = line?.thickness ?? DEFAULT_ROUTE_THICKNESS;

  const clearSelection = useCallback(() => setSelected(null), []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') clearSelection();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clearSelection]);

  // The vertex in hand as the touch listener below sees it: that listener is
  // bound once, natively, and cannot read state out of the closure it was made
  // in.
  const draggingRef = useRef(dragging);
  draggingRef.current = dragging;

  /**
   * While a press has hold of a vertex, the page underneath stays where it is.
   *
   * `touch-action` cannot be what says so: the browser settles what a touch may
   * do when the finger lands, and a photo that lets the page be scrolled with a
   * finger has already given a downward drag away by the time we know it was a
   * point being moved. Refusing the move itself is what stops it — natively and
   * non-passively, since a passive listener's preventDefault is ignored.
   */
  useEffect(() => {
    const frame = zoom.frame;
    if (!frame) return;

    function holdTheFrameStill(event: TouchEvent) {
      if (draggingRef.current !== null && event.cancelable) event.preventDefault();
    }

    frame.addEventListener('touchmove', holdTheFrameStill, { passive: false });
    return () => frame.removeEventListener('touchmove', holdTheFrameStill);
  }, [zoom.frame]);

  // A selection is an index, so a shorter line would leave it pointing past the
  // end — and the delete button would then look armed while removing nothing.
  useEffect(() => {
    setSelected((current) => (current !== null && current >= points.length ? null : current));
  }, [points.length]);

  function emit(nextPoints: Point[], next: Partial<RouteLine> = {}) {
    onChange(nextPoints.length === 0 ? null : { points: nextPoints, corridor, thickness, ...next });
  }

  /** Where a pointer landed, in the photo's natural pixels. */
  function pointerPixels(event: ReactPointerEvent): Point | null {
    const point = zoom.toContent(event.clientX, event.clientY);
    if (!point) return null;

    return {
      x: clampFraction(point.x) * naturalWidth,
      y: clampFraction(point.y) * naturalHeight,
    };
  }

  function toFraction(point: Point): Point {
    return { x: point.x / naturalWidth, y: point.y / naturalHeight };
  }

  function handlePointerDown(event: ReactPointerEvent) {
    activePointers.current.add(event.pointerId);

    // A second finger down is the picture being moved or pinched, not the line
    // being drawn: it abandons the vertex the first was dragging, and marks the
    // gesture so lifting it does not also drop a point on the wall.
    if (activePointers.current.size > 1) {
      multiTouchRef.current = true;
      setDragging(null);
      return;
    }

    const point = pointerPixels(event);
    if (!point) return;

    multiTouchRef.current = false;
    movedRef.current = false;

    // A vertex under the press wins it outright — and this press is now moving
    // that vertex rather than the picture under it.
    const vertex = lineVertexAt(points, point, naturalWidth, naturalHeight, zoom.scale);
    if (vertex >= 0) {
      zoom.cancelPan();
      setDragging(vertex);
      setSelected(vertex);
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    // Landing on the line itself splits it there and grabs the new vertex.
    const insertAt = lineSegmentAt(points, point, naturalWidth, naturalHeight, zoom.scale);
    if (insertAt >= 0) {
      zoom.cancelPan();
      const next = points.slice();
      next.splice(insertAt, 0, toFraction(point));
      emit(next);
      setDragging(insertAt);
      setSelected(insertAt);
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    // Anything else landed on bare wall: the pan the zoom armed stays armed, and
    // lifting without having travelled is what carries the line on instead.
  }

  function handlePointerMove(event: ReactPointerEvent) {
    // A press on bare wall that travels while the photo is enlarged was the
    // picture being dragged, so releasing it must not also drop a point where
    // the drag began. `moved` is what handlePointerUp reads to decide.
    if (dragging === null) {
      if (zoom.zoomed) movedRef.current = true;
      return;
    }

    const point = pointerPixels(event);
    if (!point) return;

    movedRef.current = true;
    emit(points.map((p, index) => (index === dragging ? toFraction(point) : p)));
  }

  function handlePointerUp(event: ReactPointerEvent) {
    activePointers.current.delete(event.pointerId);
    const wasMultiTouch = multiTouchRef.current;
    // The next gesture starts fresh once every finger is off the photo.
    if (activePointers.current.size === 0) multiTouchRef.current = false;

    if (dragging !== null) {
      setDragging(null);
      // A vertex that was grabbed and put straight back down is still a vertex,
      // not a new point.
      movedRef.current = false;
      return;
    }

    if (movedRef.current || wasMultiTouch) return;

    const point = pointerPixels(event);
    if (!point) return;

    emit([...points, toFraction(point)]);
    setSelected(points.length);
  }

  /**
   * The browser has taken the gesture over — the page is scrolling under the
   * finger — so nothing it had in hand was the line being drawn. Reading down a
   * page with a finger that started on the photo must not leave a point behind.
   */
  function abandonGesture(event: ReactPointerEvent) {
    activePointers.current.delete(event.pointerId);
    if (activePointers.current.size === 0) multiTouchRef.current = false;
    setDragging(null);
    movedRef.current = false;
  }

  function deleteSelected() {
    if (selected === null) return;

    emit(points.filter((_, index) => index !== selected));
    setSelected(null);
  }

  function undo() {
    emit(points.slice(0, -1));
  }

  function clearLine() {
    onChange(null);
    setSelected(null);
  }

  function setCorridor(next: number) {
    emit(points, { corridor: next });
  }

  function setThickness(next: number) {
    emit(points, { thickness: next });
  }

  return {
    zoom,
    line,
    points,
    corridor,
    thickness,
    /** Whether there is enough of a line to draw — and so to save. */
    drawable: isDrawableLine(line),
    dragging,
    selected,
    setSelected,
    clearSelection,
    deleteSelected,
    undo,
    clearLine,
    setCorridor,
    setThickness,
    naturalWidth,
    naturalHeight,
    frameProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: abandonGesture,
    },
  };
}
